import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import * as React from 'react';
import { useHistory } from 'react-router';
import Waypoint from 'react-waypoint';
import { toast } from 'sonner';
import { ILocationState } from '../../../../../models';
import { useErrorMessages, useFullscreenModal, useUserSession } from '../../../../../models/hooks/appStateHooks';
import { useEventLogging } from '../../../../../models/Logging';
import {
	invalidateInfiniteCampaignReportsQuery,
	useExportCampaignMutation,
	useInfiniteCampaignReportsQuery,
} from '../../../../../queries';
import { filterCancelledCampaigns } from '../../../../../utils/emailFiltering';
import { CampaignsApprovalViewModel } from '../../../../../viewmodels/AppViewModels';
import { ActionButton } from '../../../../components/ActionButton';
import { ExportConfirmationModalV2 } from '../../../../components/ExportConfirmation';
import { LoadingSpinner } from '../../../../components/LoadingSpinner';
import { EmailReportTableHeader } from '../../../../components/reporting/emails/EmailReportTableHeader';
import { GroupEmailActions } from '../../../../components/reporting/emails/GroupEmailActions';
import { GroupEmailTableRow } from '../../../../components/reporting/emails/GroupEmailTableRow';
import { GroupEmailTableRowExpandable } from '../../../../components/reporting/emails/GroupEmailTableRowExpandable';
import { ExportIcon } from '../../../../components/svgs/icons/ExportIcon';
import { toggleOn } from '../../../../styles/colors';
import { styleSheet } from './styles';

const PAGE_SIZE = 100;

export const groupCampaigns = (allCampaigns: Api.ICampaign[]) => {
	const grouped: (Api.ICampaign | Api.ICampaign[])[] = [];
	const groupIndexes: Record<string, number> = {};

	allCampaigns.map(campaign => {
		if (campaign.groupId) {
			if (campaign.groupId in groupIndexes) {
				(grouped[groupIndexes[campaign.groupId]] as Api.ICampaign[]).push(campaign);
			} else {
				grouped.push([campaign]);
				groupIndexes[campaign.groupId] = grouped.length - 1;
			}
		} else {
			grouped.push(campaign);
		}
	});

	return grouped;
};

export function CampaignReportingGroupedEmailsTable({
	selectedUserId,
	selectedSendStatusList,
}: {
	selectedSendStatusList: Api.EmailSendStatus[];
	selectedUserId?: string;
}) {
	const userSession = useUserSession();
	const fullscreenModal = useFullscreenModal();
	const history = useHistory();
	const { logInput, logApiError } = useEventLogging('CampaignReportingGroupedEmailsTable');
	const errorMessages = useErrorMessages();
	const [selectedCampaignGroupIds, setSelectedCampaignGroupIds] = React.useState([]);
	const [selectedCampaignIds, setSelectedCampaignIds] = React.useState([]);
	const [campaignToReschedule, setCampaignToReschedule] = React.useState<Api.ICampaign>();
	const [campaignToCancel, setCampaignToCancel] = React.useState<Api.ICampaign>();
	const [launchingCampaignApproval, setLaunchingCampaignApproval] = React.useState(false);
	const showExportButton = selectedCampaignIds.length > 0 || selectedCampaignGroupIds.length > 0;
	const reportsQueryFilter: Api.IFilterCriteria<Api.BulkEmailFilterProperty> = {
		criteria: [
			{
				property: Api.BulkEmailFilterProperty.OnlyCampaigns,
			},
		],
		op: Api.FilterOperator.And,
	};
	if (selectedUserId) {
		reportsQueryFilter.criteria.push({
			property: Api.BulkEmailFilterProperty.User,
			value: selectedUserId,
		});
	}
	const statusFilterCriteria: Api.IFilterCriteria<Api.BulkEmailFilterProperty> = {
		op: Api.FilterOperator.Or,
		criteria: [],
	};
	selectedSendStatusList.forEach(status => {
		statusFilterCriteria.criteria.push({
			property: Api.BulkEmailFilterProperty.Status,
			value: status,
		});
	});
	reportsQueryFilter.criteria.push(statusFilterCriteria);
	const campaignReportsQuery = useInfiniteCampaignReportsQuery({
		userId: selectedUserId,
		filter: reportsQueryFilter,
		pageSize: PAGE_SIZE,
	});
	const invalidateCampaignReportsQuery = () => {
		invalidateInfiniteCampaignReportsQuery({
			userId: selectedUserId,
			filter: reportsQueryFilter,
			pageSize: PAGE_SIZE,
		});
	};
	const exportCampaignMutation = useExportCampaignMutation({
		onError: error => {
			errorMessages.pushApiError(error);
			exportCampaignMutation.reset();
		},
	});
	const onCancelClicked = (_campaignToCancel?: Api.ICampaign) => (e?: React.MouseEvent<HTMLElement>) => {
		e?.stopPropagation();
		e?.preventDefault();

		setCampaignToCancel(_campaignToCancel);
	};
	const onGroupItemClick = (campaign: Api.ICampaign) => {
		const campaignVm = new Api.CampaignViewModel(userSession, campaign);
		if (
			[Api.EmailSendStatus.Ready, Api.EmailSendStatus.Queued, Api.EmailSendStatus.WaitingForApproval].indexOf(
				campaign.status
			) >= 0
		) {
			// show status modal instead of redirecting
			const locationState: ILocationState<CampaignsApprovalViewModel | Api.CampaignViewModel, any> = {
				viewModel:
					campaign.status !== Api.EmailSendStatus.WaitingForApproval
						? campaignVm
						: new CampaignsApprovalViewModel(userSession, [campaignVm]),
			};
			fullscreenModal?.history.push({
				pathname: '/email/campaigns/edit',
				state: locationState,
			});
			return;
		}
		history.push({
			pathname: `/reporting/group-email/${campaign.id}`,
			state: { viewModel: campaignVm },
		});
	};
	const handleEditGroupClicked = (id: string) => {
		fullscreenModal?.history.push({
			pathname: '/email/campaigns/create/from-queued-campaign',
			state: {
				groupId: id,
			},
		});
	};
	const mounted = React.useRef(false);
	React.useEffect(() => {
		mounted.current = true;
		return () => {
			mounted.current = false;
		};
	});
	const launchCampaignApproval = (campaign: Api.ICampaign) => {
		if (launchingCampaignApproval) {
			toast.error('Loading campaign content...');
			return;
		}
		setLaunchingCampaignApproval(true);
		const campaignVm = new Api.CampaignViewModel(userSession, campaign);
		const openModal = () => {
			const locationState: ILocationState<CampaignsApprovalViewModel, any> = {
				viewModel: new CampaignsApprovalViewModel(userSession, [campaignVm]),
			};
			fullscreenModal?.history.push({
				pathname: '/email/campaigns/edit',
				state: locationState,
			});
		};
		const promise = campaignVm.loadDefaultMessageContent();
		if (!promise) {
			setLaunchingCampaignApproval(false);
			return;
		}
		promise
			.then(() => {
				if (mounted.current) {
					openModal();
				}
			})
			.catch((error: Api.IOperationResultNoValue) => {
				logApiError('CampaignContentLoad-Error', error);
				errorMessages.pushApiError(error);
			})
			.finally(() => {
				setLaunchingCampaignApproval(false);
			});
	};
	const editCampaign = (campaignToEdit: Api.ICampaign) => {
		const campaignVm = new Api.CampaignViewModel(userSession, campaignToEdit);
		const locationState: ILocationState<CampaignsApprovalViewModel | Api.CampaignViewModel, any> = {
			viewModel:
				campaignToEdit.status !== Api.EmailSendStatus.WaitingForApproval
					? campaignVm
					: new CampaignsApprovalViewModel(userSession, [campaignVm]),
		};
		fullscreenModal?.history.push({
			pathname: '/email/campaigns/edit',
			state: locationState,
		});
	};
	const onEditClick = (campaignToEdit: Api.ICampaign) => (e?: React.MouseEvent<HTMLElement>) => {
		e?.stopPropagation();
		e?.preventDefault();
		editCampaign(campaignToEdit);
	};
	const handleGroupCampaignCheckChange = (campaigns: Api.ICampaign[]) => (checked: boolean) => {
		const ids = selectedCampaignGroupIds.filter(id => id !== campaigns[0].groupId);
		setSelectedCampaignGroupIds(checked ? [...ids, campaigns[0].groupId] : ids);
	};
	const handleCampaignCheckChange = (item: Api.ICampaign) => (checked: boolean) => {
		const ids = selectedCampaignIds.filter(id => id !== item.id);
		setSelectedCampaignIds(checked ? [...ids, item.id] : ids);
	};
	const onRescheduleClicked = (_campaignToReschedule?: Api.ICampaign) => (e?: React.MouseEvent<HTMLElement>) => {
		e?.stopPropagation();
		e?.preventDefault();
		setCampaignToReschedule(_campaignToReschedule);
	};
	const renderCampaignRows = () => {
		const allCampaigns = campaignReportsQuery.data.pages.map(page => page.values).flat();
		const isExplicitlyCancelledFilter = selectedSendStatusList.includes(Api.EmailSendStatus.Cancelled);
		const filteredCampaigns = filterCancelledCampaigns(allCampaigns, isExplicitlyCancelledFilter);
		const groupedCampaigns = groupCampaigns(filteredCampaigns);

		return groupedCampaigns.map(campaignOrGroup => {
			const campaign = Array.isArray(campaignOrGroup) ? campaignOrGroup[0] : campaignOrGroup;

			if (Array.isArray(campaignOrGroup) && campaignOrGroup.length > 1) {
				return (
					<GroupEmailTableRowExpandable
						campaigns={campaignOrGroup}
						key={`group-${campaign.groupId}`}
						onCancelClicked={onCancelClicked}
						onClick={onGroupItemClick}
						onEditClicked={editCampaign}
						onRescheduleClicked={setCampaignToReschedule}
						onCheckChanged={handleGroupCampaignCheckChange(campaignOrGroup)}
						editGroupClicked={
							!campaign?.categories?.includes(Api.EmailCategories.OnBehalf) ? handleEditGroupClicked : undefined
						}
					/>
				);
			}

			return (
				<GroupEmailTableRow
					key={campaign.id}
					onCancelClicked={onCancelClicked(campaign)}
					onClick={() => {
						if (campaign.status === Api.EmailSendStatus.WaitingForApproval) {
							launchCampaignApproval(campaign);
						} else {
							onGroupItemClick(campaign);
						}
					}}
					onEditClicked={onEditClick(campaign)}
					onRescheduleClicked={onRescheduleClicked(campaign)}
					campaign={campaign}
					onCheckChanged={handleCampaignCheckChange(campaign)}
				/>
			);
		});
	};

	return (
		<div className={css(styleSheet.container)}>
			{showExportButton ? (
				<div className={css(styleSheet.actionBar)}>
					<ActionButton
						onClick={() => {
							if (exportCampaignMutation.isIdle) {
								logInput('Export', 'Click', { groupIds: selectedCampaignGroupIds, ids: selectedCampaignIds });
								exportCampaignMutation.mutate({ ids: selectedCampaignIds, groupIds: selectedCampaignGroupIds });
							}
						}}
					>
						<ExportIcon fillColor={toggleOn} />
						<span>Export Report</span>
					</ActionButton>
				</div>
			) : null}

			{campaignReportsQuery.status === 'success' ? (
				campaignReportsQuery.data.pages[0].values.length > 0 ? (
					<>
						<EmailReportTableHeader showCheckboxes={true} />
						<div
							className={css(styleSheet.tableContainer)}
							data-scroll-restoration-id='email-reporting-campaigns-table'
						>
							{renderCampaignRows()}
							{campaignReportsQuery.hasNextPage && !campaignReportsQuery.isFetchingNextPage ? (
								<Waypoint bottomOffset='-200px' onEnter={() => campaignReportsQuery.fetchNextPage()} />
							) : null}
							{campaignReportsQuery.isFetchingNextPage ? <LoadingSpinner type='large' /> : null}
						</div>
					</>
				) : (
					<div className={css(styleSheet.noneFound)}>No campaigns found</div>
				)
			) : null}
			{campaignReportsQuery.isPending ? (
				<LoadingSpinner type='large' className={css(styleSheet.inlineLoadingSpinner)} />
			) : null}
			<GroupEmailActions
				campaignToCancel={campaignToCancel}
				campaignToReschedule={campaignToReschedule}
				loggingCategory='CampaignsContent'
				onSuccessfullyCanceledSchedule={() => {
					invalidateCampaignReportsQuery();
					setCampaignToCancel(null);
				}}
				onCancelScheduleCancellation={() => {
					setCampaignToCancel(null);
				}}
				onSuccessfullyRescheduled={() => {
					invalidateCampaignReportsQuery();
					setCampaignToReschedule(null);
				}}
				onCancelRescheduling={() => {
					setCampaignToReschedule(null);
				}}
			/>
			{!exportCampaignMutation.isIdle ? (
				<ExportConfirmationModalV2
					mutationResult={exportCampaignMutation}
					modalProps={{
						isOpen: !exportCampaignMutation.isIdle,
						onRequestClose: () => exportCampaignMutation.reset(),
					}}
					subTitle={
						<>
							<div>The fields we&apos;ll export include: First Name, Last Name,</div>
							<div>E-mail Address, Subject, Status, Sent Date, Open Date and</div>
							<div>Replied Date.</div>
						</>
					}
				/>
			) : null}
		</div>
	);
}
